import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, type ReactNode } from "react";
import { Spinner } from "@/components/states";
import { GftLogo } from "@/components/GftLogo";
import { Button } from "@/components/ui/button";
import { AUTH_DISABLED } from "./devMode";
import { currentReturnTo } from "./returnTo";

export const ALLOWED_EMAIL_DOMAIN = "gftrewards.com";

export function isAllowedEmail(email: string | undefined): boolean {
  return !!email && email.toLowerCase().endsWith(`@${ALLOWED_EMAIL_DOMAIN}`);
}

/**
 * Gates the app behind Auth0 login and a gftrewards.com email address.
 *
 * The email check here is UX only — the real enforcement lives in
 * gft-insights-api (API Gateway JWT authorizer + email-domain check),
 * so a forged client gains nothing.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, error, user, loginWithRedirect, logout } =
    useAuth0();

  useEffect(() => {
    if (!AUTH_DISABLED && !isLoading && !isAuthenticated && !error) {
      void loginWithRedirect({
        appState: { returnTo: currentReturnTo() },
      });
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect]);

  if (AUTH_DISABLED) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="brand-gradient flex min-h-screen items-center justify-center">
        <div className="flex items-center gap-3 rounded-lg bg-card px-6 py-4 text-sm text-muted-foreground">
          <GftLogo className="h-8 w-auto" />
          <Spinner />
          Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <CenteredNotice title="Sign-in failed">
        <p className="text-sm text-muted-foreground">{error.message}</p>
        <Button onClick={() => void loginWithRedirect()}>Try again</Button>
      </CenteredNotice>
    );
  }

  if (!isAuthenticated) {
    return null; // redirecting to Auth0
  }

  if (!isAllowedEmail(user?.email) || user?.email_verified !== true) {
    return (
      <CenteredNotice title="Access restricted">
        <p className="text-sm text-muted-foreground">
          {isAllowedEmail(user?.email)
            ? "Your email address has not been verified yet. Verify it and sign in again."
            : `This tool is only available to @${ALLOWED_EMAIL_DOMAIN} accounts.`}
          {user?.email ? ` You are signed in as ${user.email}.` : ""}
        </p>
        <Button
          variant="outline"
          onClick={() =>
            void logout({ logoutParams: { returnTo: window.location.origin } })
          }
        >
          Sign out
        </Button>
      </CenteredNotice>
    );
  }

  return <>{children}</>;
}

function CenteredNotice({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="brand-gradient flex min-h-screen items-center justify-center p-8">
      <div className="flex w-full max-w-md flex-col gap-4 rounded-lg bg-card p-8 text-card-foreground">
        <GftLogo className="h-10 w-auto self-start" />
        <h1 className="text-lg font-semibold">{title}</h1>
        {children}
      </div>
    </div>
  );
}
